'use client'

import { CAR_STORY_KAKAO_TEMPLATE_ID } from '@/entities/content'
import { APP_SCHEME, buildKakaoLinkPath } from '@/shared/lib'

/** 공유 메타 — 클릭 시점에 계산한다 (window.location.href는 클라에서만 유효) */
export interface IModoocarShareMeta {
  /** 공유할 절대 URL (canonical, /v2 포함) */
  url: string
  /** 카카오 카드 제목 */
  title: string
  /** 카카오 카드 설명 */
  description: string
  /** 카카오 카드 썸네일 (절대 URL) */
  imageUrl: string
}

interface IUseModoocarShareParams {
  getShareMeta: () => IModoocarShareMeta
  /** 인앱 웹뷰 여부 — 더보기를 앱 네이티브 공유 시트로 분기 */
  isInApp?: boolean
}

const KAKAO_UNAVAILABLE_MESSAGE =
  '카카오 공유를 사용할 수 없어요. 링크를 복사해 주세요.'
const LINK_COPY_SUCCESS_MESSAGE =
  '주소가 복사되었습니다.\n원하는 곳에 붙여넣기 해주세요!'

/**
 * 모두의 차고 공유 기능 — car-story 인앱 공유(useCarStoryShare) 그대로 적용.
 * - 카카오톡: Kakao JS SDK `sendCustom`(커스텀 템플릿). 인앱 웹뷰에서 `sendDefault`는
 *   4002로 막혀 동작하지 않으므로 car-story와 동일한 등록 템플릿으로 공유한다.
 * - 링크 복사: Clipboard API → execCommand 폴백.
 * - 더보기: 인앱은 앱 네이티브 공유 시트(APP_SCHEME), 웹은 Web Share API → 복사 폴백.
 */
const useModoocarShare = ({
  getShareMeta,
  isInApp = false,
}: IUseModoocarShareParams) => {
  const handleKakaoShare = () => {
    const { url, title, description, imageUrl } = getShareMeta()
    // Kakao SDK 미로드/광고 차단 환경 가드
    if (!window.Kakao?.Share) {
      alert(KAKAO_UNAVAILABLE_MESSAGE)
      return
    }

    // car-story와 동일: 커스텀 템플릿 + templateArgs(url/img/title/dsc) + installTalk.
    // url은 buildKakaoLinkPath로 도메인·basePath(/v2)를 제거 — 템플릿이 다시 붙인다.
    window.Kakao.Share.sendCustom({
      templateId: CAR_STORY_KAKAO_TEMPLATE_ID,
      templateArgs: {
        url: buildKakaoLinkPath(url),
        img: imageUrl,
        title,
        dsc: description,
      },
      installTalk: true,
    })
  }

  const handleCopyLink = async () => {
    const { url } = getShareMeta()
    // 표준 Clipboard API 우선 → 미지원 환경에서만 execCommand 폴백
    try {
      await navigator.clipboard.writeText(url)
      alert(LINK_COPY_SUCCESS_MESSAGE)
      return
    } catch {
      // 폴백으로 진행
    }

    const textarea = document.createElement('textarea')
    textarea.value = url
    textarea.readOnly = true
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    alert(LINK_COPY_SUCCESS_MESSAGE)
  }

  const handleShareMore = async () => {
    const { url, title } = getShareMeta()
    // 인앱: 앱 네이티브 공유 시트 (앱 브리지)
    if (isInApp) {
      location.href = APP_SCHEME.OPEN_SHARE_MORE(url)
      return
    }
    // 웹: Web Share API → 미지원 시 링크 복사 폴백
    if (navigator.share) {
      try {
        await navigator.share({ title, url })
      } catch {
        // 사용자가 공유 시트를 닫은 경우 등 — 무시
      }
      return
    }
    await handleCopyLink()
  }

  return { handleKakaoShare, handleCopyLink, handleShareMore }
}

export default useModoocarShare
